import * as React from 'react';

/**
 * Shared bottom offset for floating widgets (chat bubble, scanner bubble, ...).
 *
 * Keeps a fixed-position element clear of the app footer and the job progress
 * popup as the page scrolls. A single set of listeners and one measurement pass
 * are shared by every consumer, so adding another floating widget does not add
 * another scroll handler doing layout reads.
 *
 * The footer also moves with no scroll or resize: a page that settles shorter
 * after load brings it into view, and a footer that wraps grows upward while
 * the page height stays put. A ResizeObserver on the body and the footer
 * re-measures for both. See docs/chat-widget.md.
 */

const MIN_BOTTOM_OFFSET = 16;
/** Where a bubble rests when no footer or popup is in view: what page padding must clear. */
export const FOOTER_SAFE_REST_BOTTOM = MIN_BOTTOM_OFFSET;
const CLEARANCE_GAP = 8;
// A record's status bar (Focus) sits on the bottom edge while the page scrolls; the bubble rests above it.
const CLEARANCE_SELECTORS = ['.app-footer', '[data-progress-popup="1"]', '.record-status-bar'];

type Listener = (offset: number) => void;

const listeners = new Set<Listener>();
let currentOffset = MIN_BOTTOM_OFFSET;
let layoutObserver: ResizeObserver | null = null;

function measureOffset(): number {
  let offset = MIN_BOTTOM_OFFSET;
  try {
    for (const selector of CLEARANCE_SELECTORS) {
      const el = document.querySelector(selector) as HTMLElement | null;
      if (!el) continue;
      const rect = el.getBoundingClientRect();
      if (rect.top < window.innerHeight) {
        offset = Math.max(offset, Math.ceil(window.innerHeight - rect.top + CLEARANCE_GAP));
      }
    }
  } catch {}
  return offset;
}

function publish() {
  const next = measureOffset();
  if (next === currentOffset) return;
  currentOffset = next;
  listeners.forEach((listener) => listener(next));
}

function subscribe(listener: Listener): () => void {
  const isFirst = listeners.size === 0;
  listeners.add(listener);
  if (isFirst) {
    window.addEventListener('resize', publish);
    window.addEventListener('scroll', publish, { passive: true });
    window.addEventListener('progress-popup:layout', publish);
    if (typeof ResizeObserver !== 'undefined') {
      layoutObserver = new ResizeObserver(publish);
      layoutObserver.observe(document.body);
      const footer = document.querySelector(CLEARANCE_SELECTORS[0]);
      if (footer) layoutObserver.observe(footer);
    }
  }
  publish();
  // publish() only fans out on change, so sync the newcomer explicitly.
  listener(currentOffset);
  return () => {
    listeners.delete(listener);
    if (listeners.size > 0) return;
    window.removeEventListener('resize', publish);
    window.removeEventListener('scroll', publish);
    window.removeEventListener('progress-popup:layout', publish);
    layoutObserver?.disconnect();
    layoutObserver = null;
  };
}

export function useFooterSafeBottom(): number {
  const [offset, setOffset] = React.useState<number>(() =>
    typeof window === 'undefined' ? MIN_BOTTOM_OFFSET : measureOffset(),
  );
  React.useEffect(() => subscribe(setOffset), []);
  return offset;
}

export default useFooterSafeBottom;
